import axios, { AxiosResponse } from "axios";
import { getErrorMsg } from "../common/utils/request";
import { PredefinedPopupData } from "../photo-popup/interfaces/dto";

declare var pm: PmInterface;

const isLoggedIn = document.body.classList.contains("logged-in");

export default function setupLikePhoto() {
	const { findHtmlEl, findHtmlEls, getClosest } = pm.utils.dom;
	const alert = pm.utils.alert;

	const imageWrapperSelector = ".grid-image-wrapper";

	let doingAjax: boolean = false;
	let insidePopup = false;
	let predefinedPopupData: PredefinedPopupData | null = null;

	init();

	function init() {
		if (!isLoggedIn) return;
		document.addEventListener("click", handleDocumentClick);
	}

	function handleDocumentClick(e: MouseEvent) {
		const target = e.target;
		if (!(target instanceof Element)) return;

		const likeButton = getClosest(target, ".like-photo", 10);
		if (!likeButton) return;

		insidePopup = likeButton.classList.contains("inside-popup");

		if (insidePopup) {
			if (likeButton.dataset.predefinedPopupData) {
				// @ts-ignore
				if (window[likeButton.dataset.predefinedPopupData]) {
					// @ts-ignore
					predefinedPopupData = window[likeButton.dataset.predefinedPopupData];
				}
			}
		}

		let photoId = 0;

		if (likeButton.dataset.photoId) {
			photoId = parseInt(likeButton.dataset.photoId, 10);
		} else {
			const parentNode = likeButton.parentElement;

			if (parentNode?.dataset.photoId) {
				photoId = parseInt(parentNode.dataset.photoId, 10);
			}
		}

		if (!photoId) return;
		submit(photoId, likeButton);
	}

	function submit(photoId: number, button: HTMLElement) {
		if (doingAjax) return;
		doingAjax = true;

		const actionType = button.classList.contains("is-active")
			? "unlike"
			: "like";

		const totaLikesEl = findHtmlEl(button, ".total-likes");

		const prevTotalLikes =
			totaLikesEl && totaLikesEl.textContent
				? parseInt(totaLikesEl.textContent, 10)
				: 0;

		const newTotalLikes =
			actionType === "like" ? prevTotalLikes + 1 : prevTotalLikes - 1;

		// Let's visually like/unlike the photo even before the ajax request is started.
		if (actionType === "like") {
			like(photoId, newTotalLikes);
		} else {
			unlike(photoId, newTotalLikes);
		}

		const formData = new FormData();

		formData.append("action", "pm_save_like_action");
		formData.append("nonce", pm.nonces.likeOrUnlikePhoto);
		formData.append("photo_id", photoId.toString());
		formData.append("action_type", actionType);

		axios({
			method: "post",
			url: pm.ajaxurl,
			data: formData,
		})
			.then((response: AxiosResponse<any>) => {
				updateLikesCounts(photoId, response.data.data.totalLikes);
			})
			.catch((response: AxiosResponse<any>) => {
				handleFailedRequest(
					photoId,
					actionType,
					button,
					prevTotalLikes,
					response,
				);
			})
			.then(() => {
				doingAjax = false;
			});
	}

	function like(photoId: number, totalLikes: number) {
		const buttons = findHtmlEls(
			'button.like-photo[data-photo-id="' + photoId + '"]',
		);
		if (!buttons.length) return;

		buttons.forEach((button) => {
			const textSpan = findHtmlEl(button, ".button-text");
			if (textSpan) textSpan.textContent = totalLikes > 1 ? "Likes" : "Like";

			const titleEl = findHtmlEl(button, "title");
			if (titleEl) titleEl.textContent = "Unlike this photo";

			button.classList.add("is-active");
		});

		const photo = findHtmlEl(
			`${imageWrapperSelector}[data-photo-id="${photoId}"] .grid-image`,
		);

		photo?.classList.add("is-liked");

		if (insidePopup) {
			if (predefinedPopupData) {
				predefinedPopupData.photoData.isLiked = true;
			}
		}
	}

	function unlike(photoId: number, totalLikes: number) {
		const buttons = findHtmlEls(
			'button.like-photo[data-photo-id="' + photoId + '"]',
		);
		if (!buttons.length) return;

		buttons.forEach((button) => {
			const textSpan = findHtmlEl(button, ".button-text");
			if (textSpan) textSpan.textContent = totalLikes > 1 ? "Likes" : "Like";

			const titleEl = findHtmlEl(button, "title");
			if (titleEl) titleEl.textContent = "Like this photo";

			button.classList.remove("is-active");
		});

		const photo = findHtmlEl(
			`${imageWrapperSelector}[data-photo-id="${photoId}"] .grid-image`,
		);

		photo?.classList.remove("is-liked");

		if (insidePopup) {
			if (predefinedPopupData) {
				predefinedPopupData.photoData.isLiked = false;
			}
		}
	}

	function handleFailedRequest(
		photoId: number,
		actionType: string,
		button: HTMLElement,
		totalLikes: number,
		response: AxiosResponse<any>,
	) {
		const errorMsg = getErrorMsg(response);
		console.error(errorMsg);
		alert.show("error", errorMsg);

		if ("like" === actionType) {
			unlike(photoId, totalLikes);
		} else {
			like(photoId, totalLikes);
		}
	}

	function updateLikesCounts(photoId: number, totalLikes: number) {
		if (insidePopup) {
			if (predefinedPopupData) {
				predefinedPopupData.photoData.totalLikes = totalLikes;
			}
		}

		const totalLikesStr = totalLikes.toString();

		const photoWrappers = findHtmlEls(imageWrapperSelector);

		if (photoWrappers.length) {
			photoWrappers.forEach((photoWrapper) => {
				photoWrapper.dataset.totalLikes = totalLikesStr;
			});
		}

		const buttons = findHtmlEls(
			'button.like-photo[data-photo-id="' + photoId + '"]',
		);
		if (!buttons.length) return;

		buttons.forEach((button) => {
			const totalLikesEl = findHtmlEl(button, ".total-likes");

			if (totalLikesEl) {
				totalLikesEl.textContent = totalLikesStr;

				if (totalLikes > 0) {
					totalLikesEl.classList.remove("is-hidden");
				} else {
					totalLikesEl.classList.add("is-hidden");
				}
			}
		});
	}
}
